import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import "./index.css";
import { rowsToCsv } from "./csv";
import type { CCCard, DumpResponse, Preset } from "./types";
import { isValidSolanaAddress } from "./wallet";
import { parseAssetList } from "./asset-input";
import { flattenCard, resolveCellValue } from "./source-fields";
import {
  DEFAULT_PRESET,
  DEFAULT_PRESET_ID,
  duplicatePreset,
  isBuiltInPreset,
  loadStoredPresets,
  makeBlankPreset,
  parsePresetImport,
  presetsToJson,
  presetToJson,
  saveStoredPresets,
} from "./presets";
import { PresetEditor } from "./PresetEditor";
import {
  addToHistory,
  clearHistory,
  formatBytes,
  formatHistoryDate,
  getStorageMeta,
  loadHistoryAsync,
  loadHistorySync,
  removeFromHistory,
  STORAGE_SOFT_CAP_BYTES,
  trimOldestCards,
  truncateWallet,
  type HistoryEntry,
  type SaveOutcome,
  type StorageMeta,
} from "./history";
import logo from "./logo.png";

const ACTIVE_PRESET_KEY = "cc-wallet-dumper:activePreset:v1";

// Cap the live preview table — full CSV still goes to download/copy.
const PREVIEW_ROW_LIMIT = 25;

function readWalletFromUrl(): string {
  if (typeof window === "undefined") return "";
  const fromQuery = new URLSearchParams(window.location.search).get("wallet")?.trim() ?? "";
  if (fromQuery && isValidSolanaAddress(fromQuery)) return fromQuery;
  const fromPath = window.location.pathname.replace(/^\/+/, "").replace(/\/+$/, "");
  if (fromPath && isValidSolanaAddress(fromPath)) return fromPath;
  return "";
}

type Mode = "wallet" | "urls";

type LoadedSource =
  | { kind: "wallet"; wallet: string }
  | { kind: "urls"; requested: number; failed: number };

type Loaded = {
  source: LoadedSource;
  totalCards: number;
  cards: CCCard[];
};

type Status =
  | { kind: "idle" }
  | {
      kind: "loading";
      mode: "wallet";
      loaded: number;
      total: number;
      pages: { done: number; total: number };
    }
  | {
      kind: "loading";
      mode: "urls";
      loaded: number;
      total: number;
      failed: number;
    }
  | { kind: "ok"; data: Loaded }
  | { kind: "error"; message: string };

type CopyState = "idle" | "copied" | "failed";

function downloadStem(source: LoadedSource): string {
  if (source.kind === "wallet") return source.wallet;
  return `cc-assets-${source.requested}`;
}

function describeSource(source: LoadedSource): string {
  if (source.kind === "wallet") return source.wallet;
  return `${source.requested} URL${source.requested === 1 ? "" : "s"}`;
}

export function App() {
  const [wallet, setWallet] = useState(readWalletFromUrl);
  const [urlsInput, setUrlsInput] = useState("");
  const [mode, setMode] = useState<Mode>("wallet");
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [copyState, setCopyState] = useState<CopyState>("idle");
  const abortRef = useRef<AbortController | null>(null);

  // Parsed lazily so the textarea stays cheap to type into.
  const parsedUrls = useMemo(() => parseAssetList(urlsInput), [urlsInput]);

  const [presets, setPresets] = useState<Preset[]>(loadStoredPresets);
  const [activePresetId, setActivePresetId] = useState<string>(() => {
    if (typeof window === "undefined") return DEFAULT_PRESET_ID;
    return window.localStorage.getItem(ACTIVE_PRESET_KEY) ?? DEFAULT_PRESET_ID;
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [presetMessage, setPresetMessage] = useState<string | null>(null);
  const [showPresetManage, setShowPresetManage] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);

  // Sync init covers legacy uncompressed entries; the effect below
  // re-loads asynchronously to pick up gzipped envelopes.
  const [history, setHistory] = useState<HistoryEntry[]>(loadHistorySync);
  const [storageMeta, setStorageMeta] = useState<StorageMeta>(getStorageMeta);
  const [saveNotice, setSaveNotice] = useState<SaveOutcome | null>(null);

  useEffect(() => {
    loadHistoryAsync().then(setHistory).catch(() => {});
  }, []);

  const refreshStorageMeta = () => setStorageMeta(getStorageMeta());

  const allPresets = useMemo<Preset[]>(() => [DEFAULT_PRESET, ...presets], [presets]);
  const activePreset =
    allPresets.find((p) => p.id === activePresetId) ?? DEFAULT_PRESET;

  useEffect(() => {
    saveStoredPresets(presets);
  }, [presets]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(ACTIVE_PRESET_KEY, activePresetId);
  }, [activePresetId]);

  const previewRows = useMemo(() => {
    if (status.kind !== "ok") return [] as ReturnType<typeof flattenCard>[];
    return status.data.cards.slice(0, PREVIEW_ROW_LIMIT).map(flattenCard);
  }, [status]);

  const buildFullCsv = (): string => {
    if (status.kind !== "ok") return "";
    const rows = status.data.cards.map(flattenCard);
    return rowsToCsv(rows, activePreset);
  };

  const buildJson = (): string => {
    if (status.kind !== "ok") return "[]";
    return JSON.stringify(status.data.cards, null, 2);
  };

  const onSubmitWallet = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const addr = wallet.trim();
    if (!addr) return;

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setCopyState("idle");
    window.history.replaceState({}, "", `/${encodeURIComponent(addr)}`);
    setStatus({
      kind: "loading",
      mode: "wallet",
      loaded: 0,
      total: 0,
      pages: { done: 0, total: 1 },
    });

    try {
      const cards: CCCard[] = [];
      let page = 1;
      let totalCards = 0;
      let pageSize = 96;

      while (true) {
        const res = await fetch(
          `/api/dump/${encodeURIComponent(addr)}?page=${page}`,
          { signal: ctrl.signal },
        );
        const json = await res.json();
        if (!res.ok) {
          setStatus({ kind: "error", message: json.error ?? `HTTP ${res.status}` });
          return;
        }
        const body = json as DumpResponse;
        cards.push(...body.cards);
        totalCards = body.totalCards;
        pageSize = body.pageSize;

        const totalPages = Math.max(1, Math.ceil(totalCards / pageSize));
        setStatus({
          kind: "loading",
          mode: "wallet",
          loaded: cards.length,
          total: totalCards,
          pages: { done: page, total: totalPages },
        });

        if (page >= totalPages || body.cards.length === 0) break;
        page++;
      }

      setStatus({
        kind: "ok",
        data: { source: { kind: "wallet", wallet: addr }, totalCards, cards },
      });
      const outcome = await addToHistory({
        wallet: addr,
        dumpedAt: new Date().toISOString(),
        totalCards,
        cards,
      });
      setHistory(outcome.entries);
      refreshStorageMeta();
      if (outcome.evictedFromOthers > 0 || outcome.newEntryEvicted) {
        setSaveNotice(outcome);
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        setStatus({ kind: "idle" });
      } else {
        const message = err instanceof Error ? err.message : "Network error";
        setStatus({ kind: "error", message });
      }
    } finally {
      abortRef.current = null;
    }
  };

  const onSubmitUrls = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { mints } = parsedUrls;
    if (mints.length === 0) return;

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setCopyState("idle");
    // URL-mode dumps don't have a single canonical share URL, so clear
    // any leftover /<wallet> path from a previous wallet dump.
    window.history.replaceState({}, "", "/");
    setStatus({
      kind: "loading",
      mode: "urls",
      loaded: 0,
      total: mints.length,
      failed: 0,
    });

    const cards: CCCard[] = [];
    let failed = 0;

    try {
      for (let i = 0; i < mints.length; i++) {
        const mint = mints[i]!;
        try {
          const res = await fetch(`/api/asset/${encodeURIComponent(mint)}`, {
            signal: ctrl.signal,
          });
          if (!res.ok) {
            failed++;
          } else {
            const card = (await res.json()) as CCCard;
            cards.push(card);
          }
        } catch (err) {
          if (err instanceof DOMException && err.name === "AbortError") throw err;
          failed++;
        }
        setStatus({
          kind: "loading",
          mode: "urls",
          loaded: cards.length,
          total: mints.length,
          failed,
        });
      }

      setStatus({
        kind: "ok",
        data: {
          source: { kind: "urls", requested: mints.length, failed },
          totalCards: cards.length,
          cards,
        },
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        setStatus({ kind: "idle" });
      } else {
        const message = err instanceof Error ? err.message : "Network error";
        setStatus({ kind: "error", message });
      }
    } finally {
      abortRef.current = null;
    }
  };

  const onCancel = () => abortRef.current?.abort();

  const onDownload = () => {
    if (status.kind !== "ok") return;
    triggerDownload(
      buildFullCsv(),
      `${downloadStem(status.data.source)}.csv`,
      "text/csv;charset=utf-8",
    );
  };

  const onCopy = async () => {
    if (status.kind !== "ok") return;
    try {
      await navigator.clipboard.writeText(buildFullCsv());
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 1500);
    } catch {
      setCopyState("failed");
      setTimeout(() => setCopyState("idle"), 1500);
    }
  };

  const onDownloadJson = () => {
    if (status.kind !== "ok") return;
    triggerDownload(
      buildJson(),
      `${downloadStem(status.data.source)}.json`,
      "application/json",
    );
  };

  // Restore a cached entry: cards present → jump straight to result;
  // cards missing → just populate the input so the user can re-fetch.
  const onRestoreHistory = (entry: HistoryEntry) => {
    setMode("wallet");
    setWallet(entry.wallet);
    window.history.replaceState({}, "", `/${encodeURIComponent(entry.wallet)}`);
    if (entry.cards) {
      setStatus({
        kind: "ok",
        data: {
          source: { kind: "wallet", wallet: entry.wallet },
          totalCards: entry.totalCards,
          cards: entry.cards,
        },
      });
    } else {
      setStatus({ kind: "idle" });
    }
  };

  const onRemoveHistory = async (wallet: string) => {
    setHistory(await removeFromHistory(wallet));
    refreshStorageMeta();
  };

  const onClearHistory = async () => {
    if (!confirm("Clear all dump history from this browser?")) return;
    setHistory(await clearHistory());
    refreshStorageMeta();
    setSaveNotice(null);
  };

  const onTrimOldestCards = async (n: number) => {
    setHistory(await trimOldestCards(n));
    refreshStorageMeta();
    setSaveNotice(null);
  };

  const flashPresetMessage = (msg: string) => {
    setPresetMessage(msg);
    setTimeout(() => setPresetMessage(null), 2000);
  };

  const startNew = () => {
    const blank = makeBlankPreset();
    setPresets((ps) => [...ps, blank]);
    setActivePresetId(blank.id);
    setEditingId(blank.id);
    setShowPresetManage(true);
  };

  const startEdit = () => {
    if (isBuiltInPreset(activePreset)) {
      const copy = duplicatePreset(activePreset);
      copy.name = "Custom";
      setPresets((ps) => [...ps, copy]);
      setActivePresetId(copy.id);
      setEditingId(copy.id);
    } else {
      setEditingId(activePreset.id);
    }
    setShowPresetManage(true);
  };

  const startDuplicate = () => {
    const copy = duplicatePreset(activePreset);
    setPresets((ps) => [...ps, copy]);
    setActivePresetId(copy.id);
    setEditingId(copy.id);
    setShowPresetManage(true);
  };

  const onDelete = () => {
    if (isBuiltInPreset(activePreset)) return;
    if (!confirm(`Delete preset "${activePreset.name}"?`)) return;
    const id = activePreset.id;
    setPresets((ps) => ps.filter((p) => p.id !== id));
    setActivePresetId(DEFAULT_PRESET_ID);
    if (editingId === id) setEditingId(null);
  };

  const onSavePreset = (next: Preset) => {
    setPresets((ps) => ps.map((p) => (p.id === next.id ? next : p)));
    setEditingId(null);
    flashPresetMessage(`Saved "${next.name}"`);
  };

  const onExportActive = () => {
    triggerDownload(
      presetToJson(activePreset),
      `${slugify(activePreset.name) || "preset"}.json`,
      "application/json",
    );
  };

  const onExportAll = () => {
    triggerDownload(
      presetsToJson(allPresets.filter((p) => !isBuiltInPreset(p))),
      "presets.json",
      "application/json",
    );
  };

  const onImportClick = () => importInputRef.current?.click();

  const onImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const text = await file.text();
      const imported = parsePresetImport(text);
      setPresets((ps) => [...ps, ...imported]);
      flashPresetMessage(
        `Imported ${imported.length} preset${imported.length === 1 ? "" : "s"}`,
      );
      const last = imported[imported.length - 1];
      if (last) setActivePresetId(last.id);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Invalid preset file";
      flashPresetMessage(`Import failed: ${msg}`);
    }
  };

  const editingPreset = editingId
    ? allPresets.find((p) => p.id === editingId) ?? null
    : null;

  const loading = status.kind === "loading";
  const previewTruncated =
    status.kind === "ok" && status.data.cards.length > PREVIEW_ROW_LIMIT;

  return (
    <div className="max-w-3xl mx-auto p-8 relative z-10">
      <header className="mb-8 flex items-center gap-4">
        <img src={logo} alt="Collector Crypt" className="h-14 w-auto select-none" draggable={false} />
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#fbf0df]/60">Collector Crypt — Internal</p>
          <h1 className="text-4xl font-bold leading-tight">Wallet Dumper</h1>
          <p className="text-[#fbf0df]/70 mt-1">
            Export a Solana wallet's Collector Crypt cards to CSV.
          </p>
        </div>
      </header>

      <div
        role="tablist"
        aria-label="Dump source"
        className="flex gap-1 mb-2"
      >
        <button
          type="button"
          role="tab"
          aria-selected={mode === "wallet"}
          onClick={() => setMode("wallet")}
          disabled={loading}
          className={
            "text-sm px-3 py-1.5 rounded-t-md border-b-2 transition-colors duration-100 cursor-pointer disabled:cursor-not-allowed " +
            (mode === "wallet"
              ? "border-[#fbf0df] text-[#fbf0df]"
              : "border-transparent text-[#fbf0df]/50 hover:text-[#fbf0df]/80")
          }
        >
          By wallet
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "urls"}
          onClick={() => setMode("urls")}
          disabled={loading}
          className={
            "text-sm px-3 py-1.5 rounded-t-md border-b-2 transition-colors duration-100 cursor-pointer disabled:cursor-not-allowed " +
            (mode === "urls"
              ? "border-[#fbf0df] text-[#fbf0df]"
              : "border-transparent text-[#fbf0df]/50 hover:text-[#fbf0df]/80")
          }
        >
          By URL list
        </button>
      </div>

      {mode === "wallet" ? (
        <form
          onSubmit={onSubmitWallet}
          className="flex flex-col sm:flex-row items-stretch gap-2 bg-[#1a1a1a] p-3 rounded-xl border-2 border-[#fbf0df] transition-colors duration-300 focus-within:border-[#f3d5a3]"
        >
          <input
            type="text"
            name="wallet"
            value={wallet}
            onChange={(e) => setWallet(e.target.value)}
            placeholder="Solana wallet address"
            autoComplete="off"
            spellCheck={false}
            className="w-full flex-1 bg-transparent border-0 text-[#fbf0df] font-mono text-base py-1.5 px-2 outline-none focus:text-white placeholder-[#fbf0df]/40"
          />
          {loading ? (
            <button
              type="button"
              onClick={onCancel}
              className="bg-transparent text-red-300 border-2 border-red-500/60 px-5 py-2 rounded-lg font-bold transition-all duration-100 hover:bg-red-500/10 cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
          ) : (
            <button
              type="submit"
              disabled={wallet.trim() === ""}
              className="bg-[#fbf0df] text-[#1a1a1a] border-0 px-5 py-2 rounded-lg font-bold transition-all duration-100 hover:bg-[#f3d5a3] hover:-translate-y-px disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 cursor-pointer whitespace-nowrap"
            >
              Dump
            </button>
          )}
        </form>
      ) : (
        <form
          onSubmit={onSubmitUrls}
          className="flex flex-col gap-2 bg-[#1a1a1a] p-3 rounded-xl border-2 border-[#fbf0df] transition-colors duration-300 focus-within:border-[#f3d5a3]"
        >
          <textarea
            name="urls"
            value={urlsInput}
            onChange={(e) => setUrlsInput(e.target.value)}
            placeholder={
              "Paste asset URLs or mint addresses — one per line.\n" +
              "https://collectorcrypt.com/assets/solana/FSepmTSUqf3eEEgJYeYLNhLJiYShzFhMwGmoDGuRTFkN\n" +
              "AegFFWKfMV55iEBC8dncCqaDBwkmoJv8QBG8yj1XgBv8"
            }
            spellCheck={false}
            rows={6}
            className="w-full bg-transparent border-0 text-[#fbf0df] font-mono text-sm py-1.5 px-2 outline-none focus:text-white placeholder-[#fbf0df]/40 resize-y min-h-[120px]"
          />
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xs text-[#fbf0df]/60">
              {parsedUrls.mints.length} asset
              {parsedUrls.mints.length === 1 ? "" : "s"} detected
              {parsedUrls.invalid.length > 0 && (
                <span
                  className="ml-2 text-amber-300/80"
                  title={`Skipped: ${parsedUrls.invalid.slice(0, 5).join(", ")}${parsedUrls.invalid.length > 5 ? "…" : ""}`}
                >
                  · {parsedUrls.invalid.length} skipped
                </span>
              )}
            </span>
            {loading ? (
              <button
                type="button"
                onClick={onCancel}
                className="bg-transparent text-red-300 border-2 border-red-500/60 px-5 py-2 rounded-lg font-bold transition-all duration-100 hover:bg-red-500/10 cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
            ) : (
              <button
                type="submit"
                disabled={parsedUrls.mints.length === 0}
                className="bg-[#fbf0df] text-[#1a1a1a] border-0 px-5 py-2 rounded-lg font-bold transition-all duration-100 hover:bg-[#f3d5a3] hover:-translate-y-px disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 cursor-pointer whitespace-nowrap"
              >
                Fetch {parsedUrls.mints.length || ""}
              </button>
            )}
          </div>
        </form>
      )}

      {status.kind === "loading" && (
        <div className="mt-3 bg-[#1a1a1a] border border-[#fbf0df]/30 rounded-xl p-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#fbf0df]/80">
              {status.mode === "wallet"
                ? status.total > 0
                  ? `Loaded ${status.loaded} / ${status.total} cards`
                  : "Fetching first page…"
                : `Fetched ${status.loaded} / ${status.total} asset${status.total === 1 ? "" : "s"}${status.failed > 0 ? ` (${status.failed} failed)` : ""}`}
            </span>
            <span className="text-[#fbf0df]/50 text-xs">
              {status.mode === "wallet"
                ? `page ${status.pages.done} / ${status.pages.total}`
                : `asset ${Math.min(status.total, status.loaded + status.failed)} / ${status.total}`}
            </span>
          </div>
          <div className="mt-2 h-1.5 bg-[#fbf0df]/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#fbf0df] transition-all duration-200"
              style={{
                width:
                  status.total > 0
                    ? `${Math.min(100, (status.mode === "urls" ? (status.loaded + status.failed) : status.loaded) / status.total * 100)}%`
                    : "10%",
              }}
            />
          </div>
        </div>
      )}

      <section className="mt-4 flex flex-col gap-2 bg-[#1a1a1a]/40 border border-[#fbf0df]/20 rounded-xl p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-[#fbf0df]/60 shrink-0">
            Preset
          </span>
          <select
            value={activePresetId}
            onChange={(e) => setActivePresetId(e.target.value)}
            className="flex-1 min-w-[180px] bg-[#1a1a1a] border border-[#fbf0df]/40 rounded-md px-2 py-1.5 text-[#fbf0df] outline-none focus:border-[#fbf0df]"
          >
            {allPresets.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {isBuiltInPreset(p) ? " (built-in)" : ""}
              </option>
            ))}
          </select>
          <button
            onClick={startEdit}
            className="text-sm bg-transparent text-[#fbf0df] border border-[#fbf0df]/60 px-3 py-1 rounded-md hover:border-[#fbf0df] hover:bg-[#fbf0df]/10 cursor-pointer"
          >
            {isBuiltInPreset(activePreset) ? "Customize" : "Edit"}
          </button>
          <button
            onClick={() => setShowPresetManage((v) => !v)}
            className="text-xs uppercase tracking-wider text-[#fbf0df]/60 hover:text-[#fbf0df] cursor-pointer px-2"
            aria-label="Toggle preset management"
          >
            {showPresetManage ? "Less ▴" : "More ▾"}
          </button>
        </div>

        <p className="text-xs text-[#fbf0df]/50">
          {activePreset.columns.length} column
          {activePreset.columns.length === 1 ? "" : "s"}:{" "}
          {activePreset.columns.map((c) => c.header).join(", ")}
          {activePreset.description && ` — ${activePreset.description}`}
        </p>

        {showPresetManage && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={startNew}
              className="text-sm bg-transparent text-[#fbf0df] border border-[#fbf0df]/60 px-3 py-1 rounded-md hover:border-[#fbf0df] hover:bg-[#fbf0df]/10 cursor-pointer"
            >
              New
            </button>
            <button
              onClick={startDuplicate}
              className="text-sm bg-transparent text-[#fbf0df] border border-[#fbf0df]/60 px-3 py-1 rounded-md hover:border-[#fbf0df] hover:bg-[#fbf0df]/10 cursor-pointer"
            >
              Duplicate
            </button>
            <button
              onClick={onDelete}
              disabled={isBuiltInPreset(activePreset)}
              className="text-sm bg-transparent text-red-300 border border-red-500/40 px-3 py-1 rounded-md hover:border-red-400 hover:bg-red-500/10 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              Delete
            </button>
            <span className="flex-1" />
            <button
              onClick={onExportActive}
              className="text-sm bg-transparent text-[#fbf0df] border border-[#fbf0df]/60 px-3 py-1 rounded-md hover:border-[#fbf0df] hover:bg-[#fbf0df]/10 cursor-pointer"
            >
              Export
            </button>
            <button
              onClick={onExportAll}
              disabled={presets.length === 0}
              className="text-sm bg-transparent text-[#fbf0df] border border-[#fbf0df]/60 px-3 py-1 rounded-md hover:border-[#fbf0df] hover:bg-[#fbf0df]/10 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              Export all
            </button>
            <button
              onClick={onImportClick}
              className="text-sm bg-transparent text-[#fbf0df] border border-[#fbf0df]/60 px-3 py-1 rounded-md hover:border-[#fbf0df] hover:bg-[#fbf0df]/10 cursor-pointer"
            >
              Import
            </button>
            <input
              ref={importInputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={onImportFile}
            />
          </div>
        )}

        {presetMessage && (
          <p className="text-xs text-[#f3d5a3]">{presetMessage}</p>
        )}

        {editingPreset && (
          <PresetEditor
            initial={editingPreset}
            onSave={onSavePreset}
            onCancel={() => setEditingId(null)}
          />
        )}
      </section>

      {history.length > 0 && (() => {
        const usedPct = Math.min(100, (storageMeta.onDisk / STORAGE_SOFT_CAP_BYTES) * 100);
        const usageHigh = usedPct >= 80;
        const droppedCount = history.filter((h) => !h.cards).length;
        const cachedCount = history.length - droppedCount;
        const ratio =
          storageMeta.compressed && storageMeta.onDisk > 0
            ? storageMeta.raw / storageMeta.onDisk
            : 1;
        return (
          <section className="mt-4 flex flex-col gap-2 bg-[#1a1a1a]/40 border border-[#fbf0df]/20 rounded-xl p-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-xs uppercase tracking-wider text-[#fbf0df]/60">
                Recent dumps
              </span>
              <span
                className={
                  "text-xs " +
                  (usageHigh ? "text-amber-300" : "text-[#fbf0df]/50")
                }
                title={
                  storageMeta.compressed
                    ? `gzip-compressed in localStorage. ${formatBytes(storageMeta.raw)} of card data packed into ${formatBytes(storageMeta.onDisk)} on disk (${ratio.toFixed(1)}x). Browser cap is ~5 MB.`
                    : "Approximate localStorage usage. Browser cap is ~5 MB."
                }
              >
                {history.length} entr{history.length === 1 ? "y" : "ies"}
                {cachedCount < history.length && ` (${cachedCount} cached, ${droppedCount} metadata only)`}
                {" · "}
                {formatBytes(storageMeta.onDisk)} of ~{formatBytes(STORAGE_SOFT_CAP_BYTES)} ({usedPct.toFixed(0)}%)
                {storageMeta.compressed && ratio >= 1.1 && ` · ${ratio.toFixed(1)}× gzip`}
              </span>
              <button
                onClick={onClearHistory}
                className="text-xs text-[#fbf0df]/40 hover:text-red-300 cursor-pointer"
              >
                Clear all
              </button>
            </div>

            {usageHigh && !saveNotice && (
              <div className="bg-amber-500/10 border border-amber-500/40 text-amber-200 rounded-md px-3 py-2 text-xs flex flex-wrap items-center gap-2">
                <span className="flex-1">
                  Cached dumps are using ~{usedPct.toFixed(0)}% of the typical
                  browser quota. Clear older entries before another large dump
                  to avoid losing cached data.
                </span>
                <button
                  onClick={() => onTrimOldestCards(5)}
                  className="text-xs bg-transparent text-amber-200 border border-amber-400/60 px-2 py-1 rounded-md hover:bg-amber-500/10 cursor-pointer whitespace-nowrap"
                >
                  Trim 5 oldest
                </button>
              </div>
            )}

            {saveNotice && (
              <div className="bg-amber-500/10 border border-amber-500/40 text-amber-200 rounded-md px-3 py-2 text-xs flex flex-col gap-2">
                <p>
                  {saveNotice.newEntryEvicted ? (
                    <>
                      Browser storage was full — this dump's cached data couldn't be saved.{" "}
                      <strong>Use Download CSV / raw JSON now to keep the data</strong>; the
                      history entry will need to re-fetch on restore.
                      {saveNotice.evictedFromOthers > 0 && (
                        <> ({saveNotice.evictedFromOthers} older entr{saveNotice.evictedFromOthers === 1 ? "y" : "ies"} also lost cached data trying to fit it.)</>
                      )}
                    </>
                  ) : (
                    <>
                      Made room for this dump by evicting cached cards from{" "}
                      {saveNotice.evictedFromOthers} older entr{saveNotice.evictedFromOthers === 1 ? "y" : "ies"}.
                      Their metadata is still here — restoring them will re-fetch.
                    </>
                  )}
                </p>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => onTrimOldestCards(5)}
                    className="text-xs bg-transparent text-amber-200 border border-amber-400/60 px-2 py-1 rounded-md hover:bg-amber-500/10 cursor-pointer"
                  >
                    Trim 5 oldest
                  </button>
                  <button
                    onClick={onClearHistory}
                    className="text-xs bg-transparent text-amber-200 border border-amber-400/60 px-2 py-1 rounded-md hover:bg-amber-500/10 cursor-pointer"
                  >
                    Clear all history
                  </button>
                  <span className="flex-1" />
                  <button
                    onClick={() => setSaveNotice(null)}
                    className="text-xs text-amber-200/70 hover:text-amber-200 cursor-pointer"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            <ul className="flex flex-col gap-1 max-h-[160px] overflow-auto">
              {history.map((e) => (
                <li
                  key={e.wallet + e.dumpedAt}
                  className="flex items-center gap-2 group"
                >
                  <button
                    onClick={() => onRestoreHistory(e)}
                    className="flex-1 text-left flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-[#fbf0df]/5 cursor-pointer"
                    title={e.cards ? "Restore cached dump" : "Cache was evicted to save space — click to refill the wallet input and re-fetch"}
                  >
                    <span className="font-mono text-sm text-[#fbf0df]">
                      {truncateWallet(e.wallet)}
                    </span>
                    <span className="text-xs text-[#fbf0df]/50">
                      {e.totalCards} card{e.totalCards === 1 ? "" : "s"}
                    </span>
                    {!e.cards && (
                      <span
                        className="text-xs text-amber-300/80"
                        title="Cards weren't cached (or were evicted later) to save space. Click to re-fetch."
                      >
                        not cached
                      </span>
                    )}
                    <span className="ml-auto text-xs text-[#fbf0df]/50">
                      {formatHistoryDate(e.dumpedAt)}
                    </span>
                  </button>
                  <button
                    onClick={() => onRemoveHistory(e.wallet)}
                    aria-label={`Remove ${e.wallet} from history`}
                    className="text-[#fbf0df]/30 hover:text-red-400 px-2 py-1 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          </section>
        );
      })()}

      <section className="mt-6">
        {status.kind === "error" && (
          <div className="bg-[#3a1a1a] border-2 border-red-500/60 text-red-200 rounded-xl p-4">
            <strong className="font-bold">Error:</strong> {status.message}
          </div>
        )}

        {status.kind === "ok" && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-[#fbf0df]/80">
                Found <span className="font-bold text-white">{status.data.totalCards}</span> card
                {status.data.totalCards === 1 ? "" : "s"}{" "}
                <span className="text-[#fbf0df]/50">from {describeSource(status.data.source)}</span>
                {status.data.source.kind === "urls" && status.data.source.failed > 0 && (
                  <span className="ml-2 text-amber-300/80 text-sm">
                    · {status.data.source.failed} failed
                  </span>
                )}
                .{" "}
                <span className="text-[#fbf0df]/50">
                  exporting via <span className="text-[#fbf0df]/80">{activePreset.name}</span>
                </span>
              </p>
              <div className="flex gap-2 items-center">
                <button
                  onClick={onDownload}
                  disabled={status.data.totalCards === 0}
                  className="bg-[#fbf0df] text-[#1a1a1a] border-0 px-4 py-1.5 rounded-lg font-bold transition-all duration-100 hover:bg-[#f3d5a3] hover:-translate-y-px disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  Download CSV
                </button>
                <button
                  onClick={onCopy}
                  disabled={status.data.totalCards === 0}
                  className="bg-transparent text-[#fbf0df] border-2 border-[#fbf0df] px-4 py-1.5 rounded-lg font-bold transition-all duration-100 hover:bg-[#fbf0df] hover:text-[#1a1a1a] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {copyState === "copied"
                    ? "Copied!"
                    : copyState === "failed"
                      ? "Copy failed"
                      : "Copy CSV"}
                </button>
                <button
                  onClick={onDownloadJson}
                  disabled={status.data.totalCards === 0}
                  className="text-xs text-[#fbf0df]/50 hover:text-[#fbf0df] underline-offset-2 hover:underline disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                  title="Download the raw API response as JSON"
                >
                  raw JSON
                </button>
              </div>
            </div>

            {previewTruncated && (
              <p className="text-xs text-[#fbf0df]/60">
                Preview shows first {PREVIEW_ROW_LIMIT} of {status.data.totalCards} rows. Full CSV in
                Download / Copy.
              </p>
            )}

            <div className="bg-[#1a1a1a] border-2 border-[#fbf0df] rounded-xl overflow-hidden">
              <div className="overflow-auto max-h-[480px]">
                <table className="w-full text-sm font-mono">
                  <thead className="sticky top-0 bg-[#242424] text-[#fbf0df]/80">
                    <tr>
                      <th className="px-2 py-1.5 text-right text-[#fbf0df]/40 border-b border-[#fbf0df]/20 w-10 font-normal">
                        #
                      </th>
                      {activePreset.columns.map((c, i) => (
                        <th
                          key={`${c.field}-${i}`}
                          className="px-2 py-1.5 text-left font-bold border-b border-[#fbf0df]/20 whitespace-nowrap"
                          title={c.field}
                        >
                          {c.header || c.field}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, ri) => (
                      <tr key={ri} className="even:bg-[#fbf0df]/[0.025]">
                        <td className="px-2 py-1 text-right text-[#fbf0df]/40 border-b border-[#fbf0df]/10 align-top">
                          {ri + 1}
                        </td>
                        {activePreset.columns.map((c, ci) => {
                          const value = resolveCellValue(row, c);
                          return (
                            <td
                              key={`${c.field}-${ci}`}
                              className="px-2 py-1 border-b border-[#fbf0df]/10 align-top max-w-[280px] truncate"
                              title={value}
                            >
                              {value || <span className="text-[#fbf0df]/25">—</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {previewRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={activePreset.columns.length + 1}
                          className="px-2 py-6 text-center text-[#fbf0df]/50 italic"
                        >
                          No cards to display.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export default App;

function slugify(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function triggerDownload(text: string, filename: string, mime: string) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
